import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { FORM_MODE } from "../../config/constants";
import Input from "../Shared/Input";
import FormFooter from "../Shared/FormFooter";
import FormWrapper from "../Shared/FormWrapper";
import AlertRed from "../Shared/AlertRed";
import PhoneNumberInput from "../Shared/PhoneNumberInput";
import AddressLookupNative from "../Shared/AddressLookupNative";
import { usePhoneNumber } from "../../hooks/usePhoneNumber";
import { validatePhoneNumber, formatToE164 } from "../../utility/phoneNumberUtils";
import {
  createTrimHandler,
  EMAIL_REGEX,
} from "../../utility/formValidationUtils";
import TextArea from "../Shared/TextArea";
import { mapApiError } from "../../utils/formErrorMapper";

const ClientForm = ({
  defaultClient,
  closeDrawerHandler,
  saveClient,
  patchClient,
  mode,
  onCancelHandler,
  setIsFormDirty,
}) => {
  const maxCharacters = 500;
  const [noteCharCount, setNoteCharCount] = useState(() => {
    if (defaultClient?.note) {
      return defaultClient.note.length;
    }
    return 0;
  });
  const [banner, setBanner] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [lastSubmitPayload, setLastSubmitPayload] = useState(null);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isDirty, dirtyFields },
    setError,
  } = useForm({
    defaultValues: {
      firstName: defaultClient?.firstName ? defaultClient.firstName : "",
      lastName: defaultClient?.lastName ? defaultClient.lastName : "",
      email: defaultClient?.email ? defaultClient.email : "",
      phoneNumber: defaultClient?.phoneNumber || "",
      serviceAddress: defaultClient?.serviceAddress,
      note: defaultClient?.note ? defaultClient.note : "",
    },
  });

  const { selectedCountry, onCountryChangeHandler, onInputChange, defaultCountry } = usePhoneNumber(
    defaultClient?.phoneNumber
  );

  const runSubmit = async (data) => {
    if (isSubmitting) return;
    setBanner(null);
    setLastSubmitPayload(data);
    setIsSubmitting(true);
    try {
      if (mode === FORM_MODE.CREATE) {
        let payload = data;
        if (data.phoneNumber) {
          const isValid = validatePhoneNumber(data.phoneNumber, setError, defaultCountry);
          if (!isValid) return;
          payload = {
            ...data,
            phoneNumber: formatToE164(data.phoneNumber, defaultCountry),
          };
        }
        await saveClient(payload);
      } else if (mode === FORM_MODE.UPDATE) {
        const updatedData = {};
        const { _id: id } = defaultClient;
        if (dirtyFields.firstName) updatedData.firstName = data.firstName;
        if (dirtyFields.lastName) updatedData.lastName = data.lastName;
        if (dirtyFields.email) updatedData.email = data.email;
        if (dirtyFields.phoneNumber && data.phoneNumber) {
          const isValid = validatePhoneNumber(data.phoneNumber, setError, defaultCountry);
          if (!isValid) return;
          updatedData.phoneNumber = formatToE164(data.phoneNumber, defaultCountry);
        }
        if (dirtyFields.phoneNumber && !data.phoneNumber)
          updatedData.phoneNumber = data.phoneNumber;
        if (dirtyFields.serviceAddress)
          updatedData.serviceAddress = data.serviceAddress;
        if (dirtyFields.note) updatedData.note = data.note;
        await patchClient({ id, data: { ...updatedData } });
      }
    } catch (err) {
      const result = mapApiError(err, { setError });
      if (result.handled) return;
      if (result.kind === "auth") return;
      setBanner(result.banner);
    } finally {
      setIsSubmitting(false);
    }
  };

  const onSelectAddress = (address) => {
    setValue(
      "serviceAddress",
      address ? { full: address.name, placeId: address.id } : undefined,
      { shouldDirty: true }
    );
  };

  const handleRetry = () => {
    if (lastSubmitPayload) runSubmit(lastSubmitPayload);
  };

  useEffect(() => {
    setIsFormDirty(isDirty);
  }, [isDirty, setIsFormDirty]);

  return (
    <FormWrapper onSubmit={handleSubmit(runSubmit)}>
      {banner ? (
        <div className="mb-4">
          <AlertRed
            title={banner.title}
            message={banner.message}
            onRetry={handleRetry}
          />
        </div>
      ) : null}
      <div className="grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
        <div className="col-span-full">
          <Input
            id="firstName"
            name="firstName"
            label="First name"
            placeholder="e.g., Jane"
            errormessage={errors?.firstName?.message}
            {...register("firstName", {
              onBlur: createTrimHandler(setValue, "firstName"),
              required: "First name is required",
            })}
          />
        </div>
        <div className="col-span-full">
          <Input
            id="lastName"
            name="lastName"
            label="Last name"
            optional
            placeholder="e.g., Doe"
            errormessage={errors?.lastName?.message}
            {...register("lastName", {
              onBlur: createTrimHandler(setValue, "lastName"),
            })}
          />
        </div>
        <div className="col-span-full">
          <Input
            id="email"
            name="email"
            label="Email"
            placeholder="name@example.com"
            errormessage={errors?.email?.message}
            {...register("email", {
              onBlur: createTrimHandler(setValue, "email"),
              required: "Email is required",
              pattern: {
                value: EMAIL_REGEX,
                message: "Invalid email address",
              },
            })}
          />
        </div>
        <div className="col-span-full">
          <PhoneNumberInput
            selectedCountry={selectedCountry}
            onCountryChangeHandler={onCountryChangeHandler}
            onInputChange={onInputChange}
            optional={true}
            errormessage={errors?.phoneNumber?.message}
            {...register("phoneNumber", {
              onBlur: createTrimHandler(setValue, "phoneNumber"),
            })}
          />
        </div>
        <div className="col-span-full">
          <AddressLookupNative
            key={"Address"}
            label={"Address"}
            optional
            placeholder={"Search for an address..."}
            onSelect={onSelectAddress}
            defaultValue={defaultClient?.serviceAddress?.full ? { name: defaultClient.serviceAddress.full } : null}
          />
        </div>
        <div className="col-span-full">
          <TextArea
            label={"Notes"}
            optional
            id="Note"
            name="Note"
            placeholder="Anything the team should know (allergies, access codes, preferences)"
            errormessage={errors?.note?.message}
            charCount={noteCharCount}
            maxLength={maxCharacters}
            {...register("note", {
              maxLength: {
                value: maxCharacters,
                message: `Notes cannot exceed ${maxCharacters} characters`,
              },
              onBlur: (e) => {
                setValue("note", e.target.value.trim(), {
                  shouldValidate: true,
                });
              },
              onChange: (e) => {
                setNoteCharCount(e.target.value.trim().length);
                setValue("note", e.target.value, {
                  shouldValidate: true,
                });
              },
            })}
          />
        </div>
      </div>
      <FormFooter
        primaryCaption={
          mode === FORM_MODE.CREATE ? "Create client" : "Save changes"
        }
        secondaryCaption="Cancel"
        secondaryClickHandler={onCancelHandler}
        isLoading={isSubmitting}
        requiresVerification={false}
      />
    </FormWrapper>
  );
};

export default ClientForm;
